import createCachedSelector from 're-reselect';

import {
  EntityStatus,
  RequestStatusType,
  User,
  UserConnectedLeadersListFields,
  YammerState,
  getStatus,
} from '../../state/types';
import { userConnectedLeadersListRequestStatusKey } from '../leadersManagement/actions.types';
import { getStatus as getRequestStatus } from '../requestStatuses/selectors';
import { getFulfilledUserConnectedLeaders } from '../userConnectedLeaders/selectors';
import { getFulfilledUsers } from '../users/selectors';

type GetLoadMoreUserConnectedLeadersListStatus = (state: YammerState) => EntityStatus;
export const getLoadMoreUserConnectedLeadersListStatus: GetLoadMoreUserConnectedLeadersListStatus = (state) =>
  getRequestStatus(
    state,
    RequestStatusType.UserConnectedLeadersListLoadMoreRequest,
    userConnectedLeadersListRequestStatusKey
  );

type GetUserConnectedLeadersListStatus = (state: YammerState) => EntityStatus;
export const getUserConnectedLeadersListStatus: GetUserConnectedLeadersListStatus = (state) =>
  getStatus(state.userConnectedLeadersList);

type GetUserConnectedLeadersListIdsFromState = (state: YammerState) => User['id'][];
export const getUserConnectedLeadersListIdsFromState: GetUserConnectedLeadersListIdsFromState = (state: YammerState) =>
  state.userConnectedLeadersList.fulfilled?.userConnectedLeaders ?? [];

type GetUserConnectedLeadersList = (state: YammerState) => UserConnectedLeadersListFields[];
export const getUserConnectedLeadersList: GetUserConnectedLeadersList = createCachedSelector(
  getUserConnectedLeadersListIdsFromState,
  getFulfilledUserConnectedLeaders,
  getFulfilledUsers,
  (userConnectedLeadersListIds, fulfilledUserConnectedLeaders, fulfilledUsers) =>
    userConnectedLeadersListIds.reduce<UserConnectedLeadersListFields[]>((acc, edgeId) => {
      const userConnectedLeader = fulfilledUserConnectedLeaders[edgeId];

      if (!userConnectedLeader) {
        throw new Error('Missing user connected leader data in state for user connected leaders list edge.');
      }

      const { id, totalFollowedUsers, totalFollowers, directAudienceCount } = userConnectedLeader;
      const { name, jobTitle, avatarUrlTemplate } = fulfilledUsers[id];

      acc.push({
        id,
        avatarUrlTemplate,
        name,
        jobTitle,
        totalFollowedUsers,
        totalFollowers,
        directAudienceCount,
      });

      return acc;
    }, [])
)((_state) => 'cache');

type HasNextPageOfUserConnectedLeadersListAvailable = (state: YammerState) => boolean;
export const hasNextPageOfUserConnectedLeadersListAvailable: HasNextPageOfUserConnectedLeadersListAvailable = (state) =>
  state.userConnectedLeadersList.fulfilled?.hasNextPage ?? false;

type GetUserConnectedLeadersOfficeIdList = (state: YammerState) => string[];
export const getUserConnectedLeadersOfficeIdList: GetUserConnectedLeadersOfficeIdList = createCachedSelector(
  getUserConnectedLeadersListIdsFromState,
  getFulfilledUserConnectedLeaders,
  (userConnectedLeadersListIds, fulfilledUserConnectedLeaders) =>
    userConnectedLeadersListIds.reduce<string[]>((acc, edgeId) => {
      const userConnectedLeader = fulfilledUserConnectedLeaders[edgeId];

      if (!userConnectedLeader) {
        throw new Error('Missing user connected leader data in state for user connected leaders list edge.');
      }

      const { officeUserId } = userConnectedLeader;
      if (officeUserId) {
        acc.push(officeUserId);
      }

      return acc;
    }, [])
)((_state) => 'cache');
